import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Tabs from 'components/global/Tabs';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';

import { SHIPPING_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import { faCirclePlay, faPlus, faRobot, faScaleBalanced } from '@fortawesome/pro-solid-svg-icons';

import DeliveryCompanyFeasibilitiesForm from './DeliveryCompanyFeasibilities/DeliveryCompanyFeasibilitiesForm';
import FeasibilityRuleResultForm from './FeasibilityRules/FeasibilityRuleResultForm';
import OtomationTestForm from './FeasibilityRules/OtomationTestForm';
import OtomationRuleForm from './OtomationRules/OtomationRuleForm';
import OtomationRulesSortForm from './OtomationRules/OtomationRulesSortForm';
import OtomationTestFormV2 from './OtomationRules/OtomationTestForm';

/**
 * @author @omerrkosar
 */

const ShippingOtomation = () => {
	const { pathname } = useLocation();
	const { showModal } = useModalRouter();
	const { showError, showSuccess } = useAlert();
	const isAuthorized = useAccessControl();
	const axiosPrivate = useAxiosPrivate();
	const [isLoading, setIsLoading] = useState(false);
	const { isShipmentAutomationv2InUse } = useSelector((state) => state?.user);

	const isMobile = useMediaQuery(Breakpoints.mobile);

	const runFeasibilityTest = async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(SHIPPING_AUTOMATION.RUN_FEASIBILITY_RULE_TEST, {});

			const { data, status } = response;

			if (status === 200) {
				if (data.success) {
					showSuccess({ header: 'noConflictingRule' });
				} else {
					const errors = data?.resultJSON?.errors;

					if (errors[0]?.msg === 'ordersNotInFeasibilityRule') {
						showError({
							header: errors[0]?.msg,
							message: 'checkRuleConditions'
						});
						return;
					}

					const orderArray = errors?.map((error) => error?.order);
					const ruleNameArray = errors
						?.map((error) => Object.entries(error).map(([key, value]) => (value === true ? [key] : null)))
						?.map((error) => error.filter((e) => e !== null))
						?.map((error) => error.map((e) => e[0]));

					const resultArray = orderArray
						?.map((order, index) => [{ orderDetails: order, ruleNames: ruleNameArray[index] }])
						?.map((e) => e[0]);

					showError({
						header: 'error',
						message: 'feasibilityRuleTestError',
						link: 'showFeasibilityResult',
						linkAction: () => NiceModal.show(FeasibilityRuleResultForm, { data: resultArray })
					});
				}
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				header: 'serverError',
				message: error.response.data.code || error.response.data.message
			});
		} finally {
			setIsLoading(false);
		}
	};

	const tabs = useMemo(() => {
		const allTabs = [
			{
				label: 'feasibilityRules',
				path: mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.FEASIBILITY_RULES)
			},
			{
				label: 'priorityRules',
				path: mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.PRIORITY_RULES)
			},
			{
				label: 'otomationRules',
				path: mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.OTOMATION_RULES)
			},
			{
				label: 'deliveryCompanyFeasibility',
				path: mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.DELIVERY_COMPANY_FEASIBILITY)
			}
		];
		return isShipmentAutomationv2InUse ? allTabs.slice(2) : allTabs.slice(0, 2);
	}, [isShipmentAutomationv2InUse]);

	return (
		<div>
			<PageHeader
				title="shippingOtomation"
				className="shippingOtomationList"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/automation-icon.json.gz">
				{[
					mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.FEASIBILITY_RULES),
					mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.PRIORITY_RULES)
				].includes(pathname) && (
					<>
						{isAuthorized('AdvanceShippingAutomationModule') && (
							<Button
								icon={faCirclePlay}
								label={!isMobile && 'runFeasibilityTest'}
								onClick={() => runFeasibilityTest()}
								variant="black"
								loading={isLoading}
							/>
						)}
						<Button
							icon={faRobot}
							label={!isMobile && 'otomationTest'}
							onClick={() => NiceModal.show(OtomationTestForm)}
							variant="black"
						/>
					</>
				)}
				{pathname === mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.FEASIBILITY_RULES) && (
					<Authorization path="/modal/feasibility-rule-form">
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
							variant="blue"
							className="addButton"
							icon={faPlus}
							label={!isMobile && 'addFeasibilityRule'}
							onClick={() => showModal('feasibility-rule-form')}
						/>
					</Authorization>
				)}
				{pathname === mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.PRIORITY_RULES) && (
					<Authorization path="/modal/priority-rules-form">
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
							variant="blue"
							className="addButton"
							icon={faPlus}
							label={!isMobile && 'addPriorityRule'}
							onClick={() => showModal('priority-rules-form')}
						/>
					</Authorization>
				)}
				{pathname === mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.OTOMATION_RULES) && (
					<>
						<Button
							icon={faScaleBalanced}
							label={!isMobile && 'sortRules'}
							onClick={() => NiceModal.show(OtomationRulesSortForm)}
							variant="black"
						/>
						<Button
							icon={faRobot}
							label={!isMobile && 'otomationTest'}
							onClick={() => NiceModal.show(OtomationTestFormV2)}
							variant="black"
						/>
						<Authorization path="/modal/priority-rules-form">
							<Button
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
								variant="blue"
								className="addButton"
								icon={faPlus}
								label={!isMobile && 'addOtomationRule'}
								onClick={() => NiceModal.show(OtomationRuleForm)}
							/>
						</Authorization>
					</>
				)}
				{pathname === mergePaths(PATHS.SHIPPING_AUTOMATION, PATHS.DELIVERY_COMPANY_FEASIBILITY) && (
					<>
						<Button
							icon={faRobot}
							label={!isMobile && 'otomationTest'}
							onClick={() => NiceModal.show(OtomationTestFormV2)}
							variant="black"
						/>
						<Authorization path="/modal/priority-rules-form">
							<Button
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
								variant="blue"
								className="addButton"
								icon={faPlus}
								label={!isMobile && 'addFeasibilityRule'}
								onClick={() => NiceModal.show(DeliveryCompanyFeasibilitiesForm)}
							/>
						</Authorization>
					</>
				)}
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default ShippingOtomation;
